"use client";

import { useState, useRef } from "react";
import {
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
} from "lucide-react";
import { useTranslations } from "next-intl";
import { Noticia } from "@/lib/api/datosGenerales/types/noticias";
import NoticiaShell from "./NoticiaShell";

interface NoticiaPlantillaVideoProps {
  noticia: Noticia;
  locale: "es" | "en";
  tiempoLectura: number;
  noticiasRelacionadas: Noticia[];
  basePath?: string;
  videoUrl?: string;
}

function VideoPlayer({
  src,
  poster,
}: {
  src: string;
  poster: string;
}) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [progress, setProgress] = useState(0);
  const videoRef = useRef<HTMLVideoElement>(null);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) videoRef.current.pause();
    else videoRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleTimeUpdate = () => {
    if (!videoRef.current) return;
    setProgress(
      (videoRef.current.currentTime / videoRef.current.duration) * 100,
    );
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!videoRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    videoRef.current.currentTime = pos * videoRef.current.duration;
  };

  const handleFullscreen = () => {
    videoRef.current?.requestFullscreen?.();
  };

  return (
    <div className="relative aspect-video rounded-2xl overflow-hidden bg-black group border border-gray-200">
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        className="w-full h-full object-contain"
        onTimeUpdate={handleTimeUpdate}
        onEnded={() => setIsPlaying(false)}
        playsInline
      />

      {/* Overlay center play */}
      {!isPlaying && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/30 transition-colors hover:bg-black/40"
          aria-label="Play"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 flex items-center justify-center transition-transform group-hover:scale-105">
            <Play className="w-6 h-6 md:w-8 md:h-8 text-[#102542] ml-1" />
          </span>
        </button>
      )}

      {/* Bottom controls */}
      <div className="absolute inset-x-0 bottom-0 p-4 space-y-2 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
        <div
          className="w-full h-1 bg-white/30 rounded-full cursor-pointer"
          onClick={handleProgressClick}
        >
          <div
            className="h-full bg-white rounded-full transition-all"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="flex items-center justify-between text-white">
          <div className="flex items-center gap-3">
            <button onClick={togglePlay} aria-label="Play/Pause">
              {isPlaying ? (
                <Pause className="w-5 h-5" />
              ) : (
                <Play className="w-5 h-5" />
              )}
            </button>
            <button onClick={toggleMute} aria-label="Mute/Unmute">
              {isMuted ? (
                <VolumeX className="w-5 h-5" />
              ) : (
                <Volume2 className="w-5 h-5" />
              )}
            </button>
          </div>
          <button onClick={handleFullscreen} aria-label="Fullscreen">
            <Maximize className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function NoticiaPlantillaVideo({
  videoUrl,
  ...rest
}: NoticiaPlantillaVideoProps) {
  const t = useTranslations("noticiasPage.detail");

  return (
    <NoticiaShell
      {...rest}
      extraMeta={
        <span className="inline-flex items-center gap-1.5">
          <Play className="w-3 h-3" />
          {t("templateVideo")}
        </span>
      }
      topMedia={
        videoUrl ? (
          <VideoPlayer src={videoUrl} poster={rest.noticia.cover_image} />
        ) : null
      }
    />
  );
}
